import type {
  ImapAccount,
  TestImapAccountResult,
  UpsertImapAccountRequest,
} from "@repo/shared/types";
import { Loader2, Pencil, Trash2, Zap } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { del, get, post, put } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./imap-account.i18n";

const BASE = "/internal/ai/imap-account";

export function ImapAccountTab() {
  const t = useT(T);
  const [account, setAccount] = useState<ImapAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<ImapAccount | null>(BASE);
      setAccount(data);
      setEditing(!data);
    } catch (error) {
      log.error({ err: error }, "Failed to load IMAP account");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-xs font-medium text-muted-foreground">{t.heading}</div>
      <p className="text-[10px] text-muted-foreground">{t.intro}</p>
      {editing ? (
        <ImapForm
          existing={account}
          onSaved={() => {
            setEditing(false);
            load();
          }}
          onCancel={account ? () => setEditing(false) : undefined}
        />
      ) : account ? (
        <AccountCard account={account} onEdit={() => setEditing(true)} onDeleted={load} />
      ) : null}
    </div>
  );
}

function ImapForm({
  existing,
  onSaved,
  onCancel,
}: {
  existing: ImapAccount | null;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const t = useT(T);
  const [name, setName] = useState(existing?.name ?? "");
  const [email, setEmail] = useState(existing?.email ?? "");
  const [imapHost, setImapHost] = useState(existing?.imapHost ?? "");
  const [imapPort, setImapPort] = useState<number>(existing?.imapPort ?? 993);
  const [imapSecure, setImapSecure] = useState<boolean>(existing?.imapSecure ?? true);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordRequired = !existing || !existing.hasPassword;

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedHost = imapHost.trim();
    const trimmedPassword = password.trim();
    setName(trimmedName);
    setEmail(trimmedEmail);
    setImapHost(trimmedHost);
    setPassword(trimmedPassword);

    if (!trimmedName || !trimmedEmail || !trimmedHost) return;
    if (passwordRequired && !trimmedPassword) {
      setError(t.passwordRequired);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: UpsertImapAccountRequest = {
        name: trimmedName,
        email: trimmedEmail,
        imapHost: trimmedHost,
        imapPort,
        imapSecure,
        ...(trimmedPassword ? { password: trimmedPassword } : {}),
      };
      await put<ImapAccount>(BASE, payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-md p-3 space-y-2">
      <Input
        className="h-8 text-xs"
        placeholder={t.displayName}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        className="h-8 text-xs"
        placeholder={t.emailAddress}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Input
        className="h-8 text-xs"
        placeholder={t.imapHost}
        value={imapHost}
        onChange={(e) => setImapHost(e.target.value)}
      />
      <div className="flex gap-2">
        <Input
          className="h-8 text-xs flex-1"
          type="number"
          placeholder={t.port}
          value={imapPort}
          onChange={(e) => setImapPort(Number(e.target.value) || 993)}
        />
        <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground select-none">
          <input
            type="checkbox"
            checked={imapSecure}
            onChange={(e) => setImapSecure(e.target.checked)}
          />
          TLS
        </label>
      </div>
      <Input
        className="h-8 text-xs"
        placeholder={passwordRequired ? t.password : t.passwordKeep}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      {error && <p className="text-[11px] text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-7 text-xs flex-1"
          onClick={handleSubmit}
          disabled={!name || !email || !imapHost || (passwordRequired && !password)}
          loading={saving}
        >
          {existing ? t.save : t.add}
        </Button>
        {onCancel && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onCancel}>
            {t.cancel}
          </Button>
        )}
      </div>
    </div>
  );
}

function AccountCard({
  account,
  onEdit,
  onDeleted,
}: {
  account: ImapAccount;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const t = useT(T);
  const [testing, setTesting] = useState(false);
  // { ok } rather than matching the message text: the message is translated,
  // so startsWith("Connected") would style every Norwegian success as an error.
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await post<TestImapAccountResult>(`${BASE}/test`, {});
      setTestResult(
        result.success
          ? { ok: true, text: t.connected(String(result.inboxCount ?? 0)) }
          : { ok: false, text: `${t.failed}: ${result.error}` },
      );
    } catch (error) {
      setTestResult({ ok: false, text: `${t.error}: ${error}` });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t.confirmDelete(account.email))) return;
    try {
      await del(BASE);
      onDeleted();
    } catch (error) {
      setTestResult({
        ok: false,
        text: `${t.deleteFailed}: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  };

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{account.name}</div>
          <div className="text-[10px] text-muted-foreground truncate">{account.email}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            {account.imapHost}:{account.imapPort} {account.imapSecure ? "(TLS)" : `(${t.noTls})`}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleTest}
            tooltip={t.testConnection}
            loading={testing}
          >
            <Zap className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onEdit} tooltip={t.edit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleDelete} tooltip={t.delete}>
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      </div>
      {testResult && (
        <div className={`text-[11px] ${testResult.ok ? "text-success" : "text-destructive"}`}>
          {testResult.text}
        </div>
      )}
    </div>
  );
}
